import { groupBy } from 'lodash';
import { Transaction } from 'components/TransactionAllocator';
import { CalendarDay } from 'further-ui/utils';
import { TransactionOperation } from 'further-types/lp/transaction';
import { ActionTransactionSubType } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';

type CapitalActionTransaction = {
  actionId: string;
  name: string;
  ledgerDate: CalendarDay;
  transactionTypeId: string;
  transactionSubType: string;
  operationType: TransactionOperation;
  amountIncVatTcy: number;
  amountExVatTcy: number;
  amountIncVatLcy: number;
  amountExVatLcy: number;
  commitmentId: string;
  label?: string;
  effectiveCapitalDate?: CalendarDay;
};

type GroupedCapitalActionTransactions = {
  actionId: string;
  actionName: string;
  transactions: Array<Transaction>;
};

type UseCapitalActionsTransactionsProps = {
  capitalActionsTransactions: Array<CapitalActionTransaction>;
  baseCurrency: CurrencyCode;
  savedTransactions?: Record<string, Array<Transaction>>;
};

export const useCapitalActionsTransactions = ({
  capitalActionsTransactions,
  baseCurrency,
  savedTransactions,
}: UseCapitalActionsTransactionsProps) => {
  const grouped = groupBy(capitalActionsTransactions, 'actionId');

  const groupedTransactions: Array<GroupedCapitalActionTransactions> =
    Object.entries(grouped).map(([actionId, transactions]) => {
      const actionName = transactions[0]?.name ?? '';

      // Use saved transactions if available, otherwise use the API data
      const transactionsToUse: Array<Transaction> =
        savedTransactions?.[actionId] ??
        transactions.map((t) => ({
          id: undefined,
          actionId: t.actionId,
          transactionTypeId: t.transactionTypeId,
          operationType: t.operationType,
          amountExVatLcy: t.amountExVatLcy,
          amountIncVatLcy: t.amountIncVatLcy,
          amountExVatTcy: t.amountExVatTcy,
          amountIncVatTcy: t.amountIncVatTcy,
          transactionCurrency: baseCurrency,
          transactionSubType: t.transactionSubType as ActionTransactionSubType,
          label: `${t.label} - ${t.effectiveCapitalDate?.toLabel()}`,
          preCalculatedAmounts: false,
        }));

      return {
        actionId,
        actionName,
        transactions: transactionsToUse,
      };
    });

  return {
    groupedTransactions,
  };
};
